import SqFetch from 'typescript/application/sq_fetch';
import { _handleResponse } from 'typescript/clients/utilities/handle_http_response';
import { _urlContext } from 'typescript/clients/utilities/common';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import {
  type OrderCompletionTelemetry,
  type TOrderSummary,
} from 'typescript/types/api/solidshop';

// most of these functions are controlled by the methods in this controller
// app/controllers/sq/api/checkout/order_actions_controller.rb

export const GET_ORDER_SUMMARY_BASE_URL = '/shop/api/v1/checkout/order/show';
export const UPDATE_ORDER_INFORMATION_URL =
  '/shop/api/v1/checkout/order/update';
export const COMPLETE_ORDER_URL = '/shop/api/v1/checkout/order/complete';
export const RECAPTCHA_FALLBACK_URL =
  '/shop/api/v1/checkout/order/recaptcha_fallback';

export type OrderCompletionResponse = {
  success: boolean;
  telemetry?: OrderCompletionTelemetry;
};

type RecaptchaFallbackResponse = {
  challenge_token: string;
  expires_at: number;
};

const isValidRecaptchaFallbackResponse = (
  response: unknown,
): response is RecaptchaFallbackResponse => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const payload = response as Record<string, unknown>;
  return (
    typeof payload.challenge_token === 'string' &&
    payload.challenge_token.trim().length > 0 &&
    typeof payload.expires_at === 'number'
  );
};

// Tags a rejection with its HTTP status, or as a network failure when no
// response arrived, so telemetry can say why the request failed without reading
// its message. Object rejections are tagged in place: for a 400 that is the
// parsed body, so Sentry's title for it also lists the tag keys. A non-object
// rejection is replaced by a generic Error.
const tagRequestError = (
  error: unknown,
  tag:
    | { http_status: number; invalid_response?: true }
    | { error_code: 'network_failure' },
) =>
  Object.assign(
    error && typeof error === 'object'
      ? error
      : new Error('Checkout request failed'),
    tag,
  );

/**
 * Get Order summary
 * @returns order along with some other information
 * app/interactors/information/order_summary_interactor.rb
 */
export const getOrderSummary = async ({
  countryCode,
  languageCode,
  orderNumber,
}: {
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<TOrderSummary> => {
  const response = await SqFetch.get(
    `${GET_ORDER_SUMMARY_BASE_URL}${_urlContext({
      countryCode,
      languageCode,
      orderNumber,
    })}`,
  );

  return _handleResponse(response);
};

/**
 * Update order address information
 * @returns success or error message
 */
export const updateOrderAddressInformation = async ({
  address,
  countryCode,
  languageCode,
  orderNumber,
}: {
  address: Record<string, string | boolean>;
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<{ success: boolean }> => {
  const response = await SqFetch.post(UPDATE_ORDER_INFORMATION_URL, {
    checkout_information: address,
    store_code: countryCode,
    language_code: languageCode,
    order_number: orderNumber,
  });

  return _handleResponse(response);
};

/**
 * Complete Order
 * @returns success or error message
 */
export const completeOrder = async ({
  recaptchaResponse,
  financingDisclosureAcknowledged = false,
  useSavedPaymentSource = false,
  makeDefaultCard = false,
  paymentPostalCode = '',
  countryCode,
  languageCode,
  orderNumber,
}: {
  recaptchaResponse: string;
  financingDisclosureAcknowledged?: boolean;
  useSavedPaymentSource?: boolean;
  makeDefaultCard?: boolean;
  paymentPostalCode?: string;
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<OrderCompletionResponse> => {
  let response: Response | undefined;

  try {
    response = await SqFetch.post(COMPLETE_ORDER_URL, {
      recaptcha_response: recaptchaResponse,
      financing_disclosure_acknowledged: financingDisclosureAcknowledged,
      use_saved_payment_source: useSavedPaymentSource,
      set_default_payment_source: makeDefaultCard,
      payment_postal_code: paymentPostalCode,
      store_code: countryCode,
      language_code: languageCode,
      order_number: orderNumber,
      forter_token: window.ftrToken, // see app/javascript/components/typescript/types/window.d.ts
    });

    return await _handleResponse(response);
  } catch (error) {
    // JSON parse errors quote the start of the response body. The message is
    // logged to telemetry, so it must never carry raw response text.
    const errorMessage =
      error instanceof SyntaxError
        ? 'Invalid JSON response'
        : error instanceof Error
          ? error.message
          : error &&
              typeof error === 'object' &&
              'message' in error &&
              typeof error.message === 'string'
            ? error.message
            : 'Checkout request failed';
    const wrappedError = new Error(errorMessage);

    if (error && typeof error === 'object') {
      Object.assign(wrappedError, error);
    }

    Object.assign(
      wrappedError,
      response
        ? { http_status: response.status }
        : { error_code: 'network_failure' },
    );
    throw wrappedError;
  }
};

/**
 * Request fallback challenge token when recaptcha fails
 * @returns challenge token and expiration time
 */
export const requestRecaptchaFallback = async ({
  countryCode,
  languageCode,
  orderNumber,
}: {
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<{ challenge_token: string; expires_at: number }> => {
  let response: Response;
  try {
    response = await SqFetch.post(RECAPTCHA_FALLBACK_URL, {
      store_code: countryCode,
      language_code: languageCode,
      order_number: orderNumber,
    });
  } catch (error) {
    throw tagRequestError(error, { error_code: 'network_failure' });
  }

  try {
    const fallbackResponse: unknown = await _handleResponse(response);
    if (!isValidRecaptchaFallbackResponse(fallbackResponse)) {
      throw new Error('Invalid reCAPTCHA fallback response');
    }

    return fallbackResponse;
  } catch (error) {
    // A 2xx only fails here when its payload is unusable, so mark it rather
    // than let the status read as a success.
    throw tagRequestError(error, {
      http_status: response.status,
      ...(response.ok && { invalid_response: true as const }),
    });
  }
};
